import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { VEHICLES_IMPORT } from "../api/imports";
import {
  GearType,
  Vehicle,
  VehicleListSortDir,
  VehicleListSortField,
  deadlineStatus,
  nextServiceDateStatus,
  nextServiceStatus,
  useVehicleList,
} from "../api/vehicles";
import { SortableTableHeader } from "../components/common/SortableTableHeader";
import {
  ActivePassiveFilterSelect,
  DEFAULT_ACTIVE_PASSIVE_FILTER,
  activePassiveToTriState,
  type ActivePassiveFilter,
} from "../components/common/ActivePassiveFilterSelect";
import { BranchFilterSelect } from "../components/common/BranchFilterSelect";
import { formatPlateDisplay } from "../utils/plate";
import { usePlatformBranchFilter } from "../hooks/usePlatformBranchFilter";
import ExcelImportModal from "../components/imports/ExcelImportModal";
import { KmUpdateModal } from "../components/vehicles/KmUpdateModal";
import { VehicleFormModal } from "../components/vehicles/VehicleFormModal";
import { UI_LOADING } from "../constants/uiLabels";
import { ListPaginationBar, useListPageSize, usePagedOffset } from "../components/ListPagination";
import { useRegisterListPageStats } from "../context/ListPageStatsContext";
import "./VehiclesPage.css";

const GEAR_LABEL: Record<GearType, string> = {
  manual: "Manuel",
  automatic: "Otomatik",
};

function formatDate(value: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("tr-TR", { dateStyle: "medium" });
}

export function VehiclesPage() {
  const { session } = useAuth();
  const role = session?.user?.role;
  const canManage = role === "tenant_admin" || role === "manager" || role === "office_staff";
  const canUpdateKm =
    role === "tenant_admin" || role === "manager" || role === "office_staff" || role === "instructor";
  const { isPlatformOwner, branchFilter, setBranchFilter, branches } = usePlatformBranchFilter();

  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<ActivePassiveFilter>(
    DEFAULT_ACTIVE_PASSIVE_FILTER,
  );
  const [gearFilter, setGearFilter] = useState<GearType | "">("");
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [creating, setCreating] = useState(false);
  const [kmTarget, setKmTarget] = useState<Vehicle | null>(null);
  const [pageError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [sortBy, setSortBy] = useState<VehicleListSortField>("plate_no");
  const [sortDir, setSortDir] = useState<VehicleListSortDir>("asc");

  useEffect(() => {
    const handle = setTimeout(() => {
      setAppliedSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const handleSort = (column: VehicleListSortField) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(column);
    setSortDir("asc");
  };

  const pageSize = useListPageSize();
  const { offset, setOffset } = usePagedOffset(
    JSON.stringify([appliedSearch, activeFilter, gearFilter, branchFilter, sortBy, sortDir, pageSize]),
  );

  const list = useVehicleList({
    q: appliedSearch || undefined,
    isActive: activePassiveToTriState(activeFilter),
    gearType: gearFilter || undefined,
    branchId: branchFilter || undefined,
    sort: sortBy,
    order: sortDir,
    limit: pageSize,
    offset,
  });

  const items = list.data?.items || [];
  const total = list.data?.total || 0;

  useRegisterListPageStats({
    label: "araç",
    visible: items.length,
    total,
    loading: list.loading,
  });

  const summary = useMemo(() => `${total} kayit`, [total]);

  return (
    <div className="kp-vehicles">
      <div className="kp-vehicles__header">
        <div>
          <h1>Araçlar</h1>
          <p>{summary}</p>
        </div>
        {canManage && (
          <div className="kp-vehicles__header-actions">
            <button className="kp-btn" onClick={() => setImporting(true)}>
              Excel ile Toplu Yukle
            </button>
            <button className="kp-btn kp-btn--primary" onClick={() => setCreating(true)}>
              + Yeni Arac
            </button>
          </div>
        )}
      </div>

      <div className="kp-vehicles__toolbar kp-card">
        <input
          className="kp-input"
          type="search"
          placeholder="Plaka veya marka ile ara..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <ActivePassiveFilterSelect value={activeFilter} onChange={setActiveFilter} />
        <select
          className="kp-input"
          value={gearFilter}
          onChange={(e) => setGearFilter(e.target.value as GearType | "")}
        >
          <option value="">Tum vites turleri</option>
          <option value="manual">Manuel</option>
          <option value="automatic">Otomatik</option>
        </select>
        {isPlatformOwner && (
          <BranchFilterSelect
            value={branchFilter}
            onChange={setBranchFilter}
            branches={branches}
          />
        )}
        <button className="kp-btn" onClick={list.reload} disabled={list.loading}>
          Yenile
        </button>
      </div>

      {pageError && <div className="kp-card kp-error">{pageError}</div>}

      <div className="kp-vehicles__table-wrapper kp-card">
        {list.loading && <div className="kp-loading">{UI_LOADING}</div>}
        {list.error && <div className="kp-error">{list.error}</div>}

        {!list.loading && !list.error && items.length === 0 && (
          <div className="kp-empty">Bu kriterlere uyan arac bulunamadi.</div>
        )}

        {!list.loading && !list.error && items.length > 0 && (
          <table className="kp-vehicles__table kp-data-table">
            <thead>
              <tr>
                <SortableTableHeader
                  label="Plaka"
                  column="plate_no"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Marka / Vites"
                  column="brand_model"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="KM"
                  column="current_km"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Bakım"
                  column="next_service_km"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Sigorta"
                  column="insurance_expiry_date"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Muayene"
                  column="inspection_expiry_date"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Durum"
                  column="is_active"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <th scope="col" aria-label="Aksiyonlar"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((vehicle) => {
                const insurance = deadlineStatus(vehicle.insurance_expiry_date);
                const inspection = deadlineStatus(vehicle.inspection_expiry_date);
                const service = nextServiceStatus(vehicle.current_km, vehicle.next_service_km);
                const serviceDate = nextServiceDateStatus(vehicle.next_service_date);
                const combinedTone =
                  service.tone === "expired" || serviceDate.tone === "expired"
                    ? "expired"
                    : service.tone === "very-soon" || serviceDate.tone === "very-soon"
                    ? "very-soon"
                    : service.tone === "soon" || serviceDate.tone === "soon"
                    ? "soon"
                    : service.tone === "ok" || serviceDate.tone === "ok"
                    ? "ok"
                    : "none";
                return (
                  <tr key={vehicle.id}>
                    <td>
                      <strong className="kp-vehicles__plate">
                        {formatPlateDisplay(vehicle.plate_no)}
                      </strong>
                    </td>
                    <td>
                      {vehicle.brand_model || "-"}
                      {vehicle.gear_type && (
                        <small className="kp-vehicles__sub">
                          {GEAR_LABEL[vehicle.gear_type]}
                        </small>
                      )}
                    </td>
                    <td>
                      <strong>{vehicle.current_km.toLocaleString("tr-TR")}</strong>
                      <small className="kp-vehicles__sub">km</small>
                    </td>
                    <td>
                      <div className={`kp-vehicles__deadline kp-vehicles__deadline--${combinedTone}`}>
                        <span>
                          {vehicle.next_service_km !== null
                            ? `${vehicle.next_service_km.toLocaleString("tr-TR")} km`
                            : "-"}
                          {" · "}
                          {formatDate(vehicle.next_service_date)}
                        </span>
                        <small>
                          {service.label}
                          {vehicle.next_service_date && (
                            <>
                              {" · "}
                              {serviceDate.label}
                            </>
                          )}
                        </small>
                      </div>
                    </td>
                    <td>
                      <div className={`kp-vehicles__deadline kp-vehicles__deadline--${insurance.tone}`}>
                        <span>{formatDate(vehicle.insurance_expiry_date)}</span>
                        <small>{insurance.label}</small>
                      </div>
                    </td>
                    <td>
                      <div className={`kp-vehicles__deadline kp-vehicles__deadline--${inspection.tone}`}>
                        <span>{formatDate(vehicle.inspection_expiry_date)}</span>
                        <small>{inspection.label}</small>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`kp-vehicles__status kp-vehicles__status--${
                          vehicle.is_active ? "active" : "passive"
                        }`}
                      >
                        {vehicle.is_active ? "Aktif" : "Pasif"}
                      </span>
                    </td>
                    <td>
                      <div className="kp-vehicles__row-actions">
                        {canUpdateKm && (
                          <button
                            type="button"
                            className="kp-btn kp-vehicles__action"
                            onClick={() => setKmTarget(vehicle)}
                          >
                            KM
                          </button>
                        )}
                        {canManage && (
                          <button
                            type="button"
                            className="kp-btn kp-vehicles__action"
                            onClick={() => setEditing(vehicle)}
                          >
                            Düzenle
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <ListPaginationBar
          total={total}
          offset={offset}
          limit={pageSize}
          loading={list.loading}
          onOffsetChange={setOffset}
        />
      </div>

      {(creating || editing) && (
        <VehicleFormModal
          vehicle={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            list.reload();
          }}
        />
      )}

      {kmTarget && (
        <KmUpdateModal
          vehicle={kmTarget}
          onClose={() => setKmTarget(null)}
          onSaved={() => {
            setKmTarget(null);
            list.reload();
          }}
        />
      )}

      {importing && (
        <ExcelImportModal
          title="Araç Toplu Yükleme (Excel)"
          description="Şablonu indirip dolduktan sonra Yükleyin. Zorunlu kolon: Plaka."
          endpoint={VEHICLES_IMPORT}
          successLabel={(n) => `${n} arac Başarıyla eklendi.`}
          onClose={() => setImporting(false)}
          onImported={() => {
            list.reload();
          }}
        />
      )}
    </div>
  );
}
